import { getShared, setShared } from './sharedSettings';

/**
 * Šablony smluv mají v aplikaci historicky dva klíče: Dokumenty a smlouvy používají PURCHASE_CONTRACT_*,
 * šablony vozidla B2C / B2B (a varianty dovoz). Nastavení vzhledu a podpisů se proto čte i zapisuje
 * pro celou skupinu klíčů naráz, aby platilo všude (průvodce prodejem, Dokumenty, vozidlo).
 */
const GROUPS: string[][] = [
  ['PURCHASE_CONTRACT_B2C', 'B2C', 'B2C_IMPORT'],
  ['PURCHASE_CONTRACT_B2B', 'B2B', 'B2B_IMPORT'],
];

export function settingKeysFor(templateKey: string): string[] {
  return GROUPS.find(g => g.includes(templateKey)) ?? [templateKey];
}

/** Přečte nastavení (např. prefix 'theme_') pro šablonu – první nalezená hodnota ve skupině klíčů, nejdřív vlastní klíč. */
export function readSetting(prefix: string, templateKey: string): string | null {
  const own = getShared(prefix + templateKey);
  if (own != null && own !== '') return own;
  for (const k of settingKeysFor(templateKey)) {
    const v = getShared(prefix + k);
    if (v != null && v !== '') return v;
  }
  return null;
}

/** Zapíše nastavení ke všem klíčům skupiny. */
export function writeSetting(prefix: string, templateKey: string, value: string): Promise<void[]> {
  return Promise.all(settingKeysFor(templateKey).map(k => setShared(prefix + k, value)));
}

/**
 * Klíč šablony kupní smlouvy podle typu prodeje a dovozu. Stejný klíč, který upravuje editor v Nastavení
 * (dřív průvodce prodejem četl jiné klíče, takže se úpravy smlouvy v něm neprojevily).
 */
export function saleTemplateKey(saleType: 'B2C' | 'B2B' | 'COMMISSION', isImport: boolean): string {
  if (saleType === 'COMMISSION') return 'COMMISSION_CONTRACT';
  return `${saleType}${isImport ? '_IMPORT' : ''}`;
}

// ── text šablony ────────────────────────────────────────────────────────────
// Kupní smlouva existuje pod dvěma názvy (PURCHASE_CONTRACT_B2C v Dokumentech, B2C v nastavení).
// Číst se smí z obou (první nalezená vlastní verze), zapisovat se má do obou, ať je to jedna šablona.
// Varianty „dovoz“ mají vlastní text; když ho nikdo neupravil, použije se upravená běžná smlouva.

const TEXT_READ: Record<string, string[]> = {
  PURCHASE_CONTRACT_B2C: ['PURCHASE_CONTRACT_B2C', 'B2C'],
  B2C: ['PURCHASE_CONTRACT_B2C', 'B2C'],
  B2C_IMPORT: ['B2C_IMPORT', 'PURCHASE_CONTRACT_B2C'],
  PURCHASE_CONTRACT_B2B: ['PURCHASE_CONTRACT_B2B', 'B2B'],
  B2B: ['PURCHASE_CONTRACT_B2B', 'B2B'],
  B2B_IMPORT: ['B2B_IMPORT', 'PURCHASE_CONTRACT_B2B'],
};
const TEXT_WRITE: Record<string, string[]> = {
  PURCHASE_CONTRACT_B2C: ['PURCHASE_CONTRACT_B2C', 'B2C'],
  B2C: ['PURCHASE_CONTRACT_B2C', 'B2C'],
  PURCHASE_CONTRACT_B2B: ['PURCHASE_CONTRACT_B2B', 'B2B'],
  B2B: ['PURCHASE_CONTRACT_B2B', 'B2B'],
};

/** Upravený text šablony uložený na serveru (nebo null, když šablona není upravená). */
export function readCustomTemplate(templateKey: string): string | null {
  for (const k of TEXT_READ[templateKey] ?? [templateKey]) {
    const v = getShared(`tmpl_${k}`);
    if (v != null && v !== '') return v;
  }
  return null;
}

/** Text šablony – upravený, jinak výchozí. */
export function readTemplateText(templateKey: string, defaults: Record<string, string>): string {
  return readCustomTemplate(templateKey) ?? defaults[templateKey] ?? '';
}

/** Všechny klíče, do kterých se uložená šablona zapisuje. */
export function templateWriteKeys(templateKey: string): string[] {
  return TEXT_WRITE[templateKey] ?? [templateKey];
}
